import { useI18nContext } from '../../i18n/i18n-react';
import { useCommentsActions, useMessageThreads } from '@data/hooks/comments';
import { useGroups, useUsers } from '@data/hooks/usersAndGroups';
import { Avatar } from '@components/avatar/Avatar';
import { FlexRow } from '@components/layout/Flex';
import { Popover } from '@components/popover/Popover';
import { ContextAddCommentWrap } from './Comments.style';
import { appColors } from '@base/useTheme';
import { RawDraftContentState } from 'draft-js';
import React, { CSSProperties, ReactElement, useState } from 'react';
import { getInitials } from '../../data/user/user';
import { useAuth } from '../../providers/AuthProvider';
import { Attachment, AttachmentInput } from './Attachments';
import CommentThread, { COMMMENT_TEXTAREA, createUserByIdMap, getMentionData } from './CommentThread';
import { MentionInput } from './Mentions';

export interface CommentsData {
    x: number;
    y: number;
    anchor: string;
}

interface ContextCommentsProps {
    commentsData: CommentsData;
    close: () => void;
}

export function ContextComments(props: ContextCommentsProps): ReactElement | null {
    const { commentsData } = props;
    const maxHeight = window.innerHeight - 200;
    const wrapperStyle: CSSProperties = { position: 'fixed', left: commentsData.x, top: commentsData.y };

    return (
        <Popover placement="bottom-start" open onRequestClose={props.close}>
            <Popover.Trigger>
                <div style={wrapperStyle} />
            </Popover.Trigger>
            <Popover.Content $padding="5px" noFocus>
                <div style={{ padding: '1px', width: '320px' }}>
                    <Comments commentsData={props.commentsData} maxHeight={maxHeight} close={props.close} />
                </div>
            </Popover.Content>
        </Popover>
    );
}

interface CommentsProps {
    commentsData: CommentsData;
    maxHeight: number;
    close: () => void;
}

function Comments(props: CommentsProps): ReactElement | null {
    const { user } = useAuth();
    const users = useUsers();
    const groups = useGroups();
    const mentionData = getMentionData(users, groups);
    const userMapById = createUserByIdMap(users);
    const [currCommentThreadIdx, setCurrCommentThreadIdx] = useState(0);
    const { data: commentThreads } = useMessageThreads(props.commentsData.anchor);

    if (!commentThreads || commentThreads.length === 0) {
        props.close();
        return null;
    }
    const currCommentThread = commentThreads[currCommentThreadIdx];

    let arrows = null;
    if (commentThreads.length > 1) {
        arrows = {
            current: currCommentThreadIdx + 1,
            all: commentThreads.length,
            action: (idx: number) => {
                if (idx > commentThreads.length) {
                    setCurrCommentThreadIdx(0);
                } else if (idx < 1) {
                    setCurrCommentThreadIdx(commentThreads.length - 1);
                } else {
                    setCurrCommentThreadIdx(idx - 1);
                }
            },
        };
    }
    return (
        <>
            {currCommentThread && (
                <CommentThread
                    key={currCommentThread.id + currCommentThreadIdx}
                    messageThread={currCommentThread}
                    mentionData={mentionData}
                    currentUser={user}
                    arrows={arrows}
                    maxHeight={props.maxHeight}
                    userMapById={userMapById}
                />
            )}
        </>
    );
}

interface ContextAddCommentProps {
    x: number;
    y: number;
    anchor: string;
    close: () => void;
    commit: () => void;
}

export function ContextAddComment(props: ContextAddCommentProps): ReactElement | null {
    const { LL } = useI18nContext();
    const { user } = useAuth();
    const { addMessageThread, isAddingMessageThread } = useCommentsActions();
    const users = useUsers();
    const groups = useGroups();
    const mentionData = getMentionData(users, groups);

    const [attachments, setAttachments] = useState<File[]>([]);

    async function createCommentThreadHandler(
        mentionedUserIds: number[],
        mentionedGroupIds: number[],
        rawContent: RawDraftContentState
    ) {
        if ((!rawContent.blocks.some((b) => !!b.text) && !attachments.length) || isAddingMessageThread) {
            return;
        }
        const content = JSON.stringify(rawContent);
        await addMessageThread({
            message: {
                anchor: props.anchor,
                content: content,
                mentionedUserIds: mentionedUserIds,
                mentionedUserGroupIds: mentionedGroupIds,
            },
            files: attachments.length ? attachments : undefined,
        });
        setAttachments([]);
        props.close();
        props.commit();
    }

    const wrapperStyle: CSSProperties = { position: 'fixed', left: props.x, top: props.y };

    const commentNode = (
        <ContextAddCommentWrap>
            <FlexRow $align="center">
                <Avatar
                    color="white"
                    backgroundColor={appColors.ui.dark}
                    size={32}
                    initials={getInitials(user)}
                    imageUrl={user?.avatarUrl}
                    containerStyle={{ marginRight: 10 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
                    <MentionInput
                        mentionData={mentionData}
                        style={COMMMENT_TEXTAREA}
                        placeholder={LL.REPLAY_OR_TAG_IN_COMMENT()}
                        onDone={createCommentThreadHandler}
                        autoFocus
                        disabled={isAddingMessageThread}
                        footer={
                            !!attachments.length && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
                                    {attachments.map((a, idx) => (
                                        <Attachment
                                            key={idx}
                                            id={idx}
                                            name={a.name}
                                            onRemove={(id) => setAttachments(attachments.filter((a, i) => i !== id))}
                                        />
                                    ))}
                                </div>
                            )
                        }
                    />
                    <AttachmentInput
                        disabled={isAddingMessageThread}
                        onChange={(e) => {
                            const files: File[] = [];
                            for (let i = 0; i < (e.target.files?.length ?? 0); i++) {
                                const file = e.target.files?.item(i);
                                if (file) {
                                    files.push(file);
                                }
                            }

                            setAttachments([...attachments, ...files]);
                        }}
                    />
                </div>
            </FlexRow>
        </ContextAddCommentWrap>
    );

    return (
        <Popover placement="bottom-start" open onRequestClose={props.close}>
            <Popover.Trigger>
                <div style={wrapperStyle} />
            </Popover.Trigger>
            <Popover.Content $padding="5px">
                <div style={{ padding: '1px' }}>{commentNode}</div>
            </Popover.Content>
        </Popover>
    );
}
